import { z } from 'zod';

// ============================================================================
// TYPES ET SCHÉMAS DE VALIDATION
// ============================================================================

/**
 * Schéma pour les paramètres de rate limiting
 */
export const RateLimitConfigSchema = z.object({
  windowMs: z.number().min(1000).default(60000), // 1 minute par défaut
  maxRequests: z.number().min(1).default(100),
  skipSuccessfulRequests: z.boolean().default(false),
  skipFailedRequests: z.boolean().default(false),
});

export type RateLimitConfig = z.infer<typeof RateLimitConfigSchema>;

/**
 * Schéma pour les entrées de rate limiting
 */
export const RateLimitEntrySchema = z.object({
  ip: z.string().regex(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^(?:[a-fA-F0-9:]+)$/, 'Invalid IP address'),
  endpoint: z.string(),
  requests: z.number().min(0),
  windowStart: z.number(),
  lastRequest: z.number(),
});

export type RateLimitEntry = z.infer<typeof RateLimitEntrySchema>;

/**
 * Interface pour les résultats de validation de rate limiting
 */
export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  retryAfter?: number;
}

// ============================================================================
// CONFIGURATION DE SÉCURITÉ
// ============================================================================

/**
 * Configuration par défaut pour le rate limiting par endpoint
 */
const DEFAULT_RATE_LIMITS: Record<string, RateLimitConfig> = {
  '/api/quiz': { windowMs: 60000, maxRequests: 10, skipSuccessfulRequests: false, skipFailedRequests: false },
  '/api/prospects': { windowMs: 60000, maxRequests: 50, skipSuccessfulRequests: false, skipFailedRequests: false },
  '/api/pagespeed': { windowMs: 300000, maxRequests: 5, skipSuccessfulRequests: false, skipFailedRequests: false }, // 5 minutes, 5 requêtes
  '/api/seo': { windowMs: 300000, maxRequests: 10, skipSuccessfulRequests: false, skipFailedRequests: false },
  '/api/openai': { windowMs: 60000, maxRequests: 20, skipSuccessfulRequests: false, skipFailedRequests: false },
  '/api/auth': { windowMs: 900000, maxRequests: 5, skipSuccessfulRequests: true, skipFailedRequests: false }, // 15 minutes, 5 tentatives
  'default': { windowMs: 60000, maxRequests: 100, skipSuccessfulRequests: false, skipFailedRequests: false },
};

/**
 * Clé secrète pour le chiffrement (en production, cela devrait venir d'une variable d'environnement)
 */
const ENCRYPTION_KEY = 'ColdSalesSystem2024SecretKey!'; // 32 caractères pour AES-256

// ============================================================================
// UTILITAIRES DE CHIFFREMENT
// ============================================================================

/**
 * Convertit une chaîne en ArrayBuffer
 */
function stringToArrayBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Convertit un ArrayBuffer en chaîne
 */
function arrayBufferToString(buffer: ArrayBuffer): string {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

/**
 * Convertit un ArrayBuffer en chaîne base64
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convertit une chaîne base64 en ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Génère une clé de chiffrement à partir d'une chaîne
 */
async function deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Génère un salt aléatoire
 */
function generateSalt(): ArrayBuffer {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Génère un IV (Initialization Vector) aléatoire
 */
function generateIV(): ArrayBuffer {
  return crypto.getRandomValues(new Uint8Array(12));
}

// ============================================================================
// SERVICE DE SÉCURITÉ PRINCIPAL
// ============================================================================

export class SecurityService {
  private static rateLimitStore = new Map<string, RateLimitEntry>();
  private static cleanupInterval: NodeJS.Timeout | null = null;

  /**
   * Initialise le service de sécurité
   */
  static initialize(): void {
    // Nettoie les entrées expirées toutes les 5 minutes
    if (!this.cleanupInterval) {
      this.cleanupInterval = setInterval(() => {
        this.cleanupExpiredEntries();
      }, 5 * 60 * 1000);
    }
  }

  /**
   * Nettoie les entrées de rate limiting expirées
   */
  private static cleanupExpiredEntries(): void {
    const now = Date.now();
    for (const [key, entry] of this.rateLimitStore.entries()) {
      const config = this.getRateLimitConfig(entry.endpoint);
      if (now - entry.windowStart > config.windowMs) {
        this.rateLimitStore.delete(key);
      }
    }
  }

  /**
   * Obtient la configuration de rate limiting pour un endpoint
   */
  private static getRateLimitConfig(endpoint: string): RateLimitConfig {
    // Normalise l'endpoint pour correspondre aux patterns
    const normalizedEndpoint = endpoint.split('?')[0]; // Supprime les paramètres de requête
    
    // Cherche une correspondance exacte
    if (DEFAULT_RATE_LIMITS[normalizedEndpoint]) {
      return DEFAULT_RATE_LIMITS[normalizedEndpoint];
    }
    
    // Cherche une correspondance par préfixe
    for (const [pattern, config] of Object.entries(DEFAULT_RATE_LIMITS)) {
      if (pattern !== 'default' && normalizedEndpoint.startsWith(pattern)) {
        return config;
      }
    }
    
    // Retourne la configuration par défaut
    return DEFAULT_RATE_LIMITS.default;
  }

  /**
   * Génère une clé unique pour le rate limiting
   */
  private static getRateLimitKey(ip: string, endpoint: string): string {
    return `${ip}:${endpoint}`;
  }

  /**
   * Chiffre une clé API
   */
  static async encryptApiKey(key: string): Promise<string> {
    try {
      if (!key || typeof key !== 'string') {
        throw new Error('La clé API doit être une chaîne non vide');
      }

      // Génère un salt et un IV aléatoires
      const salt = generateSalt();
      const iv = generateIV();

      // Dérive la clé de chiffrement
      const cryptoKey = await deriveKey(ENCRYPTION_KEY, salt);

      // Chiffre la clé API
      const encodedKey = stringToArrayBuffer(key);
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        cryptoKey,
        encodedKey
      );

      // Combine salt + iv + données chiffrées et encode en base64
      const combined = new Uint8Array(salt.byteLength + iv.byteLength + encryptedData.byteLength);
      combined.set(new Uint8Array(salt), 0);
      combined.set(new Uint8Array(iv), salt.byteLength);
      combined.set(new Uint8Array(encryptedData), salt.byteLength + iv.byteLength);

      return arrayBufferToBase64(combined.buffer);
    } catch (error) {
      console.error('Erreur lors du chiffrement de la clé API:', error);
      throw new Error('Impossible de chiffrer la clé API');
    }
  }

  /**
   * Déchiffre une clé API
   */
  static async decryptApiKey(encryptedKey: string): Promise<string> {
    try {
      // Vérification des entrées
      if (!encryptedKey || typeof encryptedKey !== 'string') {
        console.warn('Clé vide ou non valide fournie pour déchiffrement');
        return encryptedKey; // Retourner la clé telle quelle
      }
      
      // Si la clé ne ressemble pas à une clé chiffrée (pas de base64 valide)
      if (!encryptedKey.match(/^[A-Za-z0-9+/=]+$/)) {
        return encryptedKey; // Retourner la clé telle quelle
      }

      try {
        // Décode depuis base64
        const combined = base64ToArrayBuffer(encryptedKey);
        
        if (combined.byteLength < 29) { // 16 (salt) + 12 (iv) + 1 (au moins une donnée)
          console.warn('Données chiffrées trop courtes, format incorrect');
          return encryptedKey;
        }
        
        // Extrait salt, IV et données chiffrées
        const salt = combined.slice(0, 16);
        const iv = combined.slice(16, 28);
        const encryptedData = combined.slice(28);

        // Dérive la clé de chiffrement
        const cryptoKey = await deriveKey(ENCRYPTION_KEY, salt);

        // Déchiffre les données
        const decryptedData = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: iv },
          cryptoKey,
          encryptedData
        );

        const decryptedString = arrayBufferToString(decryptedData);
        return decryptedString;
      } catch (innerError) {
        console.error('Exception lors du processus de déchiffrement:', innerError);
        // Si l'erreur est liée au déchiffrement, retourner la clé d'origine
        // Ce cas se produit généralement quand la clé n'est pas chiffrée
        return encryptedKey;
      }
    } catch (error) {
      console.error('Erreur globale lors du déchiffrement de la clé API:', error);
      // Ne pas lever d'exception, retourner plutôt la clé d'origine pour éviter de bloquer
      return encryptedKey;
    }
  }

  /**
   * Valide le rate limiting pour une IP et un endpoint
   */
  static validateRateLimit(ip: string, endpoint: string): RateLimitResult {
    try {
      // Valide les paramètres d'entrée
      if (!ip || !endpoint) {
        throw new Error('IP et endpoint sont requis');
      }

      // Normalise l'IP (supprime les préfixes IPv6 si présents)
      const normalizedIp = ip.replace(/^::ffff:/, '');
      
      const config = this.getRateLimitConfig(endpoint);
      const key = this.getRateLimitKey(normalizedIp, endpoint);
      const now = Date.now();

      let entry = this.rateLimitStore.get(key);

      // Si aucune entrée n'existe ou si la fenêtre a expiré, crée une nouvelle entrée
      if (!entry || (now - entry.windowStart) > config.windowMs) {
        entry = {
          ip: normalizedIp,
          endpoint,
          requests: 1,
          windowStart: now,
          lastRequest: now,
        };
        this.rateLimitStore.set(key, entry);

        return {
          allowed: true,
          remaining: config.maxRequests - 1,
          resetTime: now + config.windowMs,
        };
      }

      // Met à jour l'entrée existante
      entry.requests += 1;
      entry.lastRequest = now;

      // Vérifie si la limite est dépassée
      if (entry.requests > config.maxRequests) {
        const resetTime = entry.windowStart + config.windowMs;
        const retryAfter = Math.ceil((resetTime - now) / 1000); // en secondes

        return {
          allowed: false,
          remaining: 0,
          resetTime,
          retryAfter,
        };
      }

      return {
        allowed: true,
        remaining: config.maxRequests - entry.requests,
        resetTime: entry.windowStart + config.windowMs,
      };
    } catch (error) {
      console.error('Erreur lors de la validation du rate limiting:', error);
      // En cas d'erreur, on autorise la requête pour éviter de bloquer le service
      return {
        allowed: true,
        remaining: 0,
        resetTime: Date.now() + 60000,
      };
    }
  }

  /**
   * Réinitialise le rate limiting pour une IP et un endpoint spécifiques
   */
  static resetRateLimit(ip: string, endpoint: string): boolean {
    try {
      const normalizedIp = ip.replace(/^::ffff:/, '');
      const key = this.getRateLimitKey(normalizedIp, endpoint);
      return this.rateLimitStore.delete(key);
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du rate limiting:', error);
      return false;
    }
  }

  /**
   * Obtient les statistiques de rate limiting pour une IP
   */
  static getRateLimitStats(ip: string): Array<RateLimitEntry & { config: RateLimitConfig }> {
    try {
      const normalizedIp = ip.replace(/^::ffff:/, '');
      const stats: Array<RateLimitEntry & { config: RateLimitConfig }> = [];

      for (const [, entry] of this.rateLimitStore.entries()) {
        if (entry.ip === normalizedIp) {
          const config = this.getRateLimitConfig(entry.endpoint);
          stats.push({ ...entry, config });
        }
      }

      return stats;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return [];
    }
  }

  /**
   * Nettoie toutes les entrées de rate limiting
   */
  static clearAllRateLimits(): void {
    this.rateLimitStore.clear();
  }

  /**
   * Arrête le service de sécurité et nettoie les ressources
   */
  static shutdown(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.rateLimitStore.clear();
  }

  /**
   * Valide et nettoie une clé API (supprime les espaces, vérifie le format)
   */
  static sanitizeApiKey(key: string): string {
    if (!key || typeof key !== 'string') {
      throw new Error('La clé API doit être une chaîne non vide');
    }

    // Supprime les espaces en début et fin
    const cleaned = key.trim();

    if (cleaned.length === 0) {
      throw new Error('La clé API ne peut pas être vide');
    }

    // Vérifie que la clé ne contient que des caractères autorisés
    if (!/^[a-zA-Z0-9\-_\.]+$/.test(cleaned)) {
      throw new Error('La clé API contient des caractères non autorisés');
    }

    return cleaned;
  }

  /**
   * Génère un token sécurisé pour les quiz
   */
  static generateSecureToken(length: number = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    
    return Array.from(array, byte => chars[byte % chars.length]).join('');
  }

  /**
   * Valide un token de quiz
   */
  static validateQuizToken(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }

    // Vérifie la longueur (8 caractères par défaut)
    if (token.length !== 8) {
      return false;
    }

    // Vérifie que le token contient uniquement des caractères alphanumériques
    if (!/^[a-zA-Z0-9]+$/.test(token)) {
      return false;
    }

    return true;
  }

  /**
   * Hash une chaîne avec SHA-256
   */
  static async hashString(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return arrayBufferToBase64(hashBuffer);
  }

  /**
   * Vérifie si une IP est dans une liste blanche
   */
  static isWhitelistedIP(ip: string, whitelist: string[] = []): boolean {
    const normalizedIp = ip.replace(/^::ffff:/, '');
    
    // IPs locales toujours autorisées en développement
    const localIPs = ['127.0.0.1', '::1', 'localhost'];
    if (process.env.NODE_ENV === 'development' && localIPs.includes(normalizedIp)) {
      return true;
    }

    return whitelist.includes(normalizedIp);
  }
}

// Initialise le service au chargement du module
SecurityService.initialize();

// ============================================================================
// MIDDLEWARE ET UTILITAIRES
// ============================================================================

/**
 * Middleware pour appliquer le rate limiting
 */
export function createRateLimitMiddleware() {
  return (ip: string, endpoint: string) => {
    const result = SecurityService.validateRateLimit(ip, endpoint);
    
    if (!result.allowed) {
      const error = new Error('Trop de requêtes');
      (error as any).status = 429;
      (error as any).retryAfter = result.retryAfter;
      (error as any).resetTime = result.resetTime;
      throw error;
    }

    return result;
  };
}

/**
 * Utilitaire pour extraire l'IP réelle d'une requête
 */
export function extractClientIP(headers: Record<string, string>): string {
  // Vérifie les en-têtes de proxy courants
  const forwardedFor = headers['x-forwarded-for'];
  if (forwardedFor) {
    // Prend la première IP de la liste
    return forwardedFor.split(',')[0].trim();
  }

  const realIP = headers['x-real-ip'];
  if (realIP) {
    return realIP.trim();
  }

  const clientIP = headers['x-client-ip'];
  if (clientIP) {
    return clientIP.trim();
  }

  // Fallback sur l'IP de connexion
  return headers['remote-addr'] || '127.0.0.1';
}

// ============================================================================
// EXPORT PAR DÉFAUT
// ============================================================================

export default SecurityService;
